/**
 * The logo printed at the head of the menu card.
 *
 * Kept in localStorage as a data URI rather than in Firestore. A logo changes
 * about once a year, so syncing it would spend a document read on every device
 * for something that never moves, and a data URI in a Firestore document
 * counts against the 1MB limit that the day's orders also live under.
 *
 * The cost is that it is per-device: an owner printing from a second machine
 * picks the file again. That is the right trade for a card printed a handful of
 * times a year.
 */

const KEY = 'kermes:menu-logo'

/** Beyond this a data URI starts to slow the preview's re-render noticeably. */
const MAX_BYTES = 400_000

/** Longest edge, in pixels. A menu logo prints at ~25mm; 600px is generous. */
const MAX_EDGE = 600

export function loadLogo(tenantId: string): string | null {
  try {
    return localStorage.getItem(`${KEY}:${tenantId}`)
  } catch {
    // Private browsing can refuse localStorage outright. The card prints
    // perfectly well without a logo, so this is not worth surfacing.
    return null
  }
}

export function saveLogo(tenantId: string, dataUri: string): void {
  try {
    localStorage.setItem(`${KEY}:${tenantId}`, dataUri)
  } catch (err) {
    console.error('[menu] logo could not be stored', err)
  }
}

export function clearLogo(tenantId: string): void {
  try {
    localStorage.removeItem(`${KEY}:${tenantId}`)
  } catch {
    /* nothing to clean up */
  }
}

/**
 * Reads a picked file into a data URI, shrinking it first.
 *
 * A photo straight off a phone is several megabytes, far past what
 * localStorage will hold, and enough to make the live preview stutter as it
 * re-renders on every keystroke. Redrawing through a canvas caps both.
 *
 * PNG rather than JPEG: menu logos are usually flat artwork on a transparent
 * background, and JPEG would fill that background with white and fringe the
 * edges.
 */
export async function fileToLogo(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Bu dosya bir görsel değil.')
  }

  const raw = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Dosya okunamadı.'))
    reader.readAsDataURL(file)
  })

  // SVG is already small and scales without loss; redrawing it to a canvas
  // would throw away exactly the quality it was chosen for.
  if (file.type === 'image/svg+xml') return raw

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = () => reject(new Error('Görsel açılamadı.'))
    el.src = raw
  })

  const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height))
  const w = Math.round(img.width * scale)
  const h = Math.round(img.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Görsel işlenemedi.')
  ctx.drawImage(img, 0, 0, w, h)

  const out = canvas.toDataURL('image/png')
  if (out.length > MAX_BYTES) {
    throw new Error('Görsel çok büyük. Daha küçük bir dosya seçin.')
  }
  return out
}
